import { assetRef } from '@/Manager/Asset';
import VersionHistory from './VersionHistory';

/**
 * Registry of feature announcements keyed by version number.
 * Add new announcements here when releasing new features.
 */
const ANNOUNCEMENTS: Record<string, IVersionAnnouncement[]> = {
	'3.27.0': [
		{
			id: 'feature-3.27.0-performance',
			steps: [
				{
					content: [
						'<p>You should notice a significant performance boost while using the Trainer on both iOS and Android devices.</p>',
						'<p>We will continue to improve performance over the next few months.</p>',
					].join(''),
					media: assetRef('video/feature-trainer.mp4'),
					position: 'bottom',
					title: `<span style='color: var(--chalky-red);'>Update!</span> <span>Performance Boost for Trainer</span>`,
					type: 'card',
				},
			],
		},
	],

	// '3.27.4': [
	// 	{
	// 		id: 'feature-3.27.4-performance',
	// 		steps: [
	// 			{
	// 				content: ['<p>Comments are a thing now</p>'].join(''),
	// 				media: assetRef('video/feature-trainer.mp4'),
	// 				position: 'bottom',
	// 				title: `<span style='color: var(--chalky-red);'><span>Comments are a thing now</span>`,
	// 				type: 'card',
	// 			},
	// 		],
	// 	},
	// ],
};

/**
 * Service for managing version-based feature announcements.
 * Integrates with VersionHistory to show relevant coach marks based on
 * which app versions the user has previously seen.
 *
 * @example Get and display announcements for the current user
 *
 *     const announcements = Service.FeatureAnnouncements.getAnnouncementsForUser();
 *
 *     if (announcements.length > 0) {
 *         this.showCoachMarks(announcements);
 *     }
 *
 * @example Get announcements for users upgrading from a specific version
 *
 *     const announcements = Service.FeatureAnnouncements.getAnnouncementsSinceVersion('3.25.0');
 *
 *     announcements.forEach((announcement) => {
 *         console.log(`New feature: ${announcement.id}`);
 *     });
 *
 * @example Get announcements for a specific version only
 *
 *     const v327Announcements = Service.FeatureAnnouncements.getAnnouncementsForVersion('3.27.0');
 *
 * @example Check which versions have announcements
 *
 *     const versions = Service.FeatureAnnouncements.getVersionsWithAnnouncements();
 *     // Returns: ['3.27.0', '3.27.4']
 *
 * @example Adding new announcements to the ANNOUNCEMENTS registry
 *
 *     '3.28.0': [
 *         {
 *             id: 'feature-3.28.0-new-thing',
 *             steps: [
 *                 {
 *                     content: '<p>Description of the new feature.</p>',
 *                     media: assetRef('video/feature-new-thing.mp4'),
 *                     position: 'bottom',
 *                     title: `<span style='color: var(--chalky-red);'>New!</span> Feature Name`,
 *                     type: 'card',
 *                 },
 *             ],
 *         },
 *     ],
 *
 * @author Matt Kenefick <matt@chalkysticks.com>
 * @package App/Service
 * @project ChalkySticks App
 */
export class FeatureAnnouncements {
	/**
	 * Get all announcements relevant to the current user based on their
	 * version history. Returns announcements from versions they haven't seen.
	 *
	 * @return IVersionAnnouncement[]
	 */
	public static getAnnouncementsForUser(): IVersionAnnouncement[] {
		// First launch - skip feature announcements (show onboarding instead)
		if (VersionHistory.isFirstLaunchEver()) {
			console.warn('Skipping feature announcements on first launch');
			return [];
		}

		const previousVersion = VersionHistory.getPreviousVersion();

		// No previous version means this is first launch
		// if (!previousVersion) {
		// 	console.warn('No previous version found, skipping feature announcements');
		// 	return [];
		// }

		// Get all announcements for versions newer than previousVersion
		return this.getAnnouncementsSinceVersion(previousVersion);
	}

	/**
	 * Get all announcements from versions newer than the specified version.
	 * Useful for users who skipped multiple versions.
	 *
	 * @param string sinceVersion
	 * @return IVersionAnnouncement[]
	 */
	public static getAnnouncementsSinceVersion(sinceVersion: string): IVersionAnnouncement[] {
		return Object.entries(ANNOUNCEMENTS)
			.filter(([version]) => VersionHistory.compareVersions(version, sinceVersion) > 0)
			.sort(([versionA], [versionB]) => VersionHistory.compareVersions(versionA, versionB))
			.flatMap(([, announcements]) => announcements);
	}

	/**
	 * Get announcements for a specific version only.
	 *
	 * @param string version
	 * @return IVersionAnnouncement[]
	 */
	public static getAnnouncementsForVersion(version: string): IVersionAnnouncement[] {
		return ANNOUNCEMENTS[version] || [];
	}

	/**
	 * Get all registered version numbers that have announcements.
	 *
	 * @return string[]
	 */
	public static getVersionsWithAnnouncements(): string[] {
		return Object.keys(ANNOUNCEMENTS).sort((a, b) => VersionHistory.compareVersions(a, b));
	}
}

export default FeatureAnnouncements;
